// PracticeAnalytics.jsx — Dan-only 📊 view inside Library → Practice.
// Answers three questions, in order: (1) who is booth-ready, (2) who has
// momentum, (3) how do the artists compare over time.
//
// PACE IS SPLIT FLASH vs CUSTOM (2026-08-20, Dan: a 12:57 "average" was
// blending customs into flash pace). Shift summaries only store a MIXED
// per-artist avg, so pace derives straight from finished orders
// (orderType + drawingTimeMs) — one whole-collection done-orders fetch,
// same pattern the Library gallery uses. Flash target 5:00, custom 9:00
// (the live wait-estimate baselines).
//
// Chart conventions (dataviz pass): fixed 4-slot categorical palette
// validated for the dark surface (#b57d0d amber, #3f97d6 sky, #ef537e pink,
// #8f7ad9 violet — CVD-safe, ≥3:1); color follows the ARTIST (registry
// order); one axis per chart; label text wears ink tokens; status colors
// are reserved for readiness verdicts and always ship icon + word.

import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { artistNameKeys } from "../hooks/useArtists";
import { formatDrawTime } from "../utils/practice";
import ArtistAvatar from "../components/ArtistAvatar";
import { STAFF_THEME, FONT } from "../theme/staff";

const BRAND = STAFF_THEME;
const SERIES = ["#b57d0d", "#3f97d6", "#ef537e", "#8f7ad9"];
const SERIES_OVERFLOW = "#8a93a6"; // artist 5+ (fold — still direct-labeled)

const FLASH_TARGET_MIN = 5; // BASELINE_FLASH_MIN
const CUSTOM_TARGET_MIN = 9; // BASELINE_CUSTOM_MIN

export default function PracticeAnalytics({ artists, reps, summaries }) {
  // Finished orders — the ground truth for live pace (per-type, per-shift).
  const [orders, setOrders] = useState(null); // null = loading
  useEffect(() => {
    let cancelled = false;
    getDocs(query(collection(db, "orders"), where("state", "==", "done")))
      .then((snap) => {
        if (cancelled) return;
        setOrders(snap.docs
          .map((d) => d.data())
          .filter((o) => o.artistName && !o._isTestData && !o.trashedAt));
      })
      .catch(() => { if (!cancelled) setOrders([]); });
    return () => { cancelled = true; };
  }, []);

  const shiftCloseById = useMemo(() => {
    const m = {};
    for (const s of summaries) {
      if (s.shiftId && s.closedAt?.seconds && !s._isTestData) m[s.shiftId] = { t: s.closedAt.seconds * 1000, event: s.eventName };
    }
    return m;
  }, [summaries]);

  const data = useMemo(() => {
    const active = artists.filter((a) => a.active !== false);
    return active.map((a, i) => {
      const mine = reps
        .filter((r) => r.artistId === a.id)
        .sort((x, y) => (x.createdAt?.seconds || 0) - (y.createdAt?.seconds || 0));
      const names = new Set(artistNameKeys(a));
      const myOrders = (orders || [])
        .filter((o) => names.has(o.artistName.trim().toLowerCase()))
        .sort((x, y) => (x.completedAt?.seconds || 0) - (y.completedAt?.seconds || 0));
      const timed = myOrders.filter((o) => o.drawingTimeMs > 0);
      const flash = timed.filter((o) => o.orderType !== "custom");
      const custom = timed.filter((o) => o.orderType === "custom");

      const avg = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null);
      const recentMin = (arr, n) => avg(arr.slice(-n).map((o) => o.drawingTimeMs / 60000));

      // Flash pace: live flash orders first, else griddle reps, else paper reps
      const griddleRecent = mine.filter((r) => r.medium === "griddle" && r.drawTimeMs > 0).slice(-5).map((r) => r.drawTimeMs / 60000);
      const paperRecent = mine.filter((r) => r.medium !== "griddle" && r.drawTimeMs > 0).slice(-5).map((r) => r.drawTimeMs / 60000);
      const flashPace = recentMin(flash, 10) ?? avg(griddleRecent) ?? avg(paperRecent);
      const flashSource = flash.length ? "live" : griddleRecent.length ? "griddle" : paperRecent.length ? "paper" : null;
      const customPace = recentMin(custom, 10);

      // Per-shift pace points, per type
      const byShift = {};
      for (const o of timed) {
        if (!o.shiftId) continue;
        (byShift[o.shiftId] = byShift[o.shiftId] || { flash: [], custom: [] })[o.orderType === "custom" ? "custom" : "flash"].push(o.drawingTimeMs / 60000);
      }
      const mkPoints = (kind) => Object.entries(byShift)
        .map(([shiftId, g]) => {
          if (!g[kind].length) return null;
          const close = shiftCloseById[shiftId];
          const t = close?.t
            || Math.max(...timed.filter((o) => o.shiftId === shiftId).map((o) => (o.completedAt?.seconds || 0) * 1000));
          if (!t) return null;
          return { t, min: avg(g[kind]), n: g[kind].length, event: close?.event };
        })
        .filter(Boolean)
        .sort((x, y) => x.t - y.t);
      const flashPoints = mkPoints("flash");
      const customPoints = mkPoints("custom");

      // Quality — last 10 reviewed reps
      const rated = mine.filter((r) => r.review?.rating).slice(-10);
      const qualAvg = rated.length ? rated.reduce((s, r) => s + r.review.rating, 0) / rated.length : null;

      // Verdict — flash pace (staffing = flash throughput) + quality
      const enough = rated.length >= 3 || flash.length > 0;
      let verdict;
      if (!enough) verdict = { icon: "⚪", word: "Needs data", color: BRAND.dim };
      else {
        const paceOk = flashPace != null && flashPace <= FLASH_TARGET_MIN;
        const paceClose = flashPace != null && flashPace <= FLASH_TARGET_MIN * 1.5;
        const qualOk = qualAvg == null ? true : qualAvg >= 4;
        const qualClose = qualAvg == null ? true : qualAvg >= 3;
        if (paceOk && qualOk) verdict = { icon: "🟢", word: "Booth-ready", color: BRAND.green };
        else if (paceClose && qualClose) verdict = { icon: "🟡", word: "Getting close", color: BRAND.gold };
        else verdict = { icon: "🔴", word: "Not yet", color: BRAND.red };
      }

      // Momentum: reps per week, last 12 weeks
      const now = Date.now();
      const weeks = Array.from({ length: 12 }, (_, w) => {
        const end = now - w * 7 * 86400000;
        const start = end - 7 * 86400000;
        return mine.filter((r) => {
          const t = r.createdAt?.seconds ? r.createdAt.seconds * 1000 : 0;
          return t > start && t <= end;
        }).length;
      }).reverse();
      const last4 = weeks.slice(-4).reduce((s, v) => s + v, 0);
      const prev4 = weeks.slice(-8, -4).reduce((s, v) => s + v, 0);

      const bestFlash = flash.reduce((b, o) => (b == null || o.drawingTimeMs < b ? o.drawingTimeMs : b), null);
      const lastActive = mine.length ? mine[mine.length - 1].createdAt?.seconds * 1000 : null;

      return {
        artist: a,
        color: i < SERIES.length ? SERIES[i] : SERIES_OVERFLOW,
        mine, flashPace, flashSource, customPace, flashCount: flash.length, customCount: custom.length,
        flashPoints, customPoints, qualAvg, ratedCount: rated.length,
        verdict, weeks, last4, prev4, liveTotal: myOrders.length, bestFlash, lastActive,
      };
    });
  }, [artists, reps, orders, shiftCloseById]);

  if (data.length === 0) {
    return <div style={{ color: BRAND.dim, fontSize: "0.85rem", padding: "1rem 0" }}>Add artists to the registry to see analytics.</div>;
  }
  if (orders === null) {
    return <div style={{ color: BRAND.dim, fontSize: "0.85rem", padding: "1rem 0" }}>Crunching pancake history…</div>;
  }

  return (
    <div>
      {/* ── 1. Booth readiness ─────────────────────────────────────────── */}
      <Label>Booth readiness — ⚡ flash target {FLASH_TARGET_MIN}:00 · 🎨 custom target {CUSTOM_TARGET_MIN}:00</Label>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1.3rem" }}>
        {data.map((d) => (
          <div key={d.artist.id} style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "12px", padding: "0.6rem 0.8rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.45rem" }}>
              <ArtistAvatar avatar={d.artist.avatar} fallbackEmoji={d.artist.emoji || "🥞"} size={28} />
              <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: d.color, flexShrink: 0 }} />
              <span style={{ fontWeight: 800, fontFamily: FONT.display, fontSize: "0.88rem", flex: 1 }}>{d.artist.name}</span>
              <span style={{ color: d.verdict.color, fontSize: "0.76rem", fontWeight: 800 }}>{d.verdict.icon} {d.verdict.word}</span>
            </div>
            <PaceBullet
              label="⚡ flash" paceMin={d.flashPace} targetMin={FLASH_TARGET_MIN} scaleMax={10} color={d.color}
              note={d.flashSource === "live" ? `last ${Math.min(10, d.flashCount)} live` : d.flashSource === "griddle" ? "griddle reps (no live flash yet)" : d.flashSource === "paper" ? "paper reps only" : null}
            />
            {d.customPace != null && (
              <div style={{ marginTop: "0.35rem" }}>
                <PaceBullet
                  label="🎨 custom" paceMin={d.customPace} targetMin={CUSTOM_TARGET_MIN} scaleMax={20} color={d.color}
                  note={`last ${Math.min(10, d.customCount)} live`}
                />
              </div>
            )}
            <div style={{ color: BRAND.muted, fontSize: "0.7rem", marginTop: "0.35rem" }}>
              {d.qualAvg != null
                ? <>quality: avg {d.qualAvg.toFixed(1)}★ over last {d.ratedCount} reviewed</>
                : "quality: no reviewed reps yet"}
            </div>
          </div>
        ))}
      </div>

      {/* ── 2. Momentum ────────────────────────────────────────────────── */}
      <Label>Momentum — reps per week, last 12 weeks</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0.6rem", marginBottom: "1.3rem" }}>
        {data.map((d) => {
          const max = Math.max(1, ...d.weeks);
          const trend = d.last4 > d.prev4 ? { t: "▲ warming up", c: BRAND.green } : d.last4 < d.prev4 ? { t: "▼ cooling off", c: BRAND.pink } : { t: "— steady", c: BRAND.dim };
          return (
            <div key={d.artist.id} style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "12px", padding: "0.6rem 0.8rem" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", marginBottom: "0.35rem" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: d.color, flexShrink: 0, alignSelf: "center" }} />
                <span style={{ fontWeight: 800, fontFamily: FONT.display, fontSize: "0.82rem", flex: 1 }}>{d.artist.name}</span>
                <span style={{ fontSize: "0.66rem", fontWeight: 800, color: trend.c }}>{trend.t}</span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: "3px", height: "36px" }} title={`${d.last4} reps last 4 weeks (${d.prev4} the 4 before)`}>
                {d.weeks.map((n, i) => (
                  <div key={i} title={`${n} reps`} style={{
                    flex: 1, borderRadius: "2px 2px 0 0",
                    height: `${Math.max(6, (n / max) * 100)}%`,
                    background: n > 0 ? d.color : "rgba(255,255,255,0.08)",
                  }} />
                ))}
              </div>
              <div style={{ color: BRAND.dim, fontSize: "0.64rem", marginTop: "0.2rem" }}>
                {d.mine.length} reps total · {d.last4} in the last 4 weeks
              </div>
            </div>
          );
        })}
      </div>

      {/* ── 3. Comparison — pace over time, split by type ─────────────── */}
      <PaceChart data={data} />

      {/* Table view (also the accessibility fallback for the chart) */}
      <div style={{ overflowX: "auto", marginTop: "1rem" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.74rem" }}>
          <thead>
            <tr style={{ color: BRAND.dim, textAlign: "left" }}>
              {["Artist", "Reps", "Live 🥞", "⚡ avg", "🎨 avg", "Avg ★", "Best ⚡", "Last practice"].map((h) => (
                <th key={h} style={{ padding: "0.3rem 0.5rem", borderBottom: `1px solid ${BRAND.border}`, fontWeight: 700 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.artist.id} style={{ color: BRAND.text }}>
                <td style={{ padding: "0.35rem 0.5rem", fontWeight: 700 }}>
                  <span style={{ display: "inline-block", width: "9px", height: "9px", borderRadius: "2px", background: d.color, marginRight: "0.35rem" }} />
                  {d.artist.name}
                </td>
                <td style={{ padding: "0.35rem 0.5rem" }}>{d.mine.length}</td>
                <td style={{ padding: "0.35rem 0.5rem" }}>{d.liveTotal}</td>
                <td style={{ padding: "0.35rem 0.5rem" }}>{d.flashCount ? fmtMin(avgOf(d.flashPoints.flatMap((p) => Array(p.n).fill(p.min)))) : "—"}</td>
                <td style={{ padding: "0.35rem 0.5rem" }}>{d.customPace != null ? fmtMin(d.customPace) : "—"}</td>
                <td style={{ padding: "0.35rem 0.5rem" }}>{d.qualAvg != null ? d.qualAvg.toFixed(1) : "—"}</td>
                <td style={{ padding: "0.35rem 0.5rem" }}>{d.bestFlash != null ? formatDrawTime(d.bestFlash) : "—"}</td>
                <td style={{ padding: "0.35rem 0.5rem", color: BRAND.muted }}>
                  {d.lastActive ? new Date(d.lastActive).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const avgOf = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null);
const fmtMin = (min) => min == null ? "—" : `${Math.floor(min)}:${String(Math.round((min % 1) * 60)).padStart(2, "0")}`;

// Pace bullet: recent pace bar against a target tick.
function PaceBullet({ label, paceMin, targetMin, scaleMax, color, note }) {
  if (paceMin == null) {
    return <div style={{ color: BRAND.dim, fontSize: "0.7rem" }}>{label}: no timed work yet</div>;
  }
  const pct = Math.min(100, (paceMin / scaleMax) * 100);
  const targetPct = (targetMin / scaleMax) * 100;
  return (
    <div>
      <div style={{ position: "relative", height: "13px", background: "rgba(0,0,0,0.4)", borderRadius: "7px" }}>
        <div style={{ position: "absolute", left: 0, top: "3px", bottom: "3px", width: `${pct}%`, borderRadius: "4px", background: color, opacity: 0.9 }} />
        <div title={`target ${targetMin}:00`} style={{ position: "absolute", left: `${targetPct}%`, top: "-2px", bottom: "-2px", width: "2px", background: BRAND.cream }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", color: BRAND.muted, fontSize: "0.66rem", marginTop: "0.12rem" }}>
        <span>{label} avg {fmtMin(paceMin)}{note && <span style={{ color: BRAND.dim }}> ({note})</span>}</span>
        <span style={{ color: BRAND.dim }}>target {targetMin}:00 · scale 0–{scaleMax}m</span>
      </div>
    </div>
  );
}

// Multi-series line chart: per-shift avg draw minutes per artist, with a
// ⚡ flash / 🎨 custom mode toggle (one measure on the axis at a time).
function PaceChart({ data }) {
  const [mode, setMode] = useState("flash");
  const [tip, setTip] = useState(null);
  const target = mode === "flash" ? FLASH_TARGET_MIN : CUSTOM_TARGET_MIN;
  const series = data
    .map((d) => ({ ...d, pts: mode === "flash" ? d.flashPoints : d.customPoints }))
    .filter((d) => d.pts.length > 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.5rem" }}>
        <Label style={{ margin: 0, flex: 1 }}>Booth pace by shift — avg draw minutes (lower is faster)</Label>
        <ModeChip active={mode === "flash"} onClick={() => { setMode("flash"); setTip(null); }} label="⚡ Flash" />
        <ModeChip active={mode === "custom"} onClick={() => { setMode("custom"); setTip(null); }} label="🎨 Custom" />
      </div>
      {series.length === 0 ? (
        <div style={{ color: BRAND.dim, fontSize: "0.78rem", padding: "0.5rem 0 1rem" }}>
          No shifts with timed {mode} pancakes yet — this fills in as artists work live events.
        </div>
      ) : (
        <ChartBody series={series} target={target} tip={tip} setTip={setTip} />
      )}
    </div>
  );
}

function ChartBody({ series, target, tip, setTip }) {
  const W = 720, H = 240, padL = 40, padR = 86, padT = 14, padB = 26;
  const allPts = series.flatMap((s) => s.pts);
  const tMin = Math.min(...allPts.map((p) => p.t));
  const tMax = Math.max(...allPts.map((p) => p.t));
  const span = Math.max(1, tMax - tMin);
  const yMax = Math.max(target * 2, Math.ceil(Math.max(...allPts.map((p) => p.min)) + 1));
  const x = (t) => padL + ((t - tMin) / span) * (W - padL - padR);
  const y = (min) => padT + (1 - min / yMax) * (H - padT - padB);
  const fmtD = (t) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const yStep = yMax > 12 ? 4 : 2;

  return (
    <div style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "12px", padding: "0.7rem 0.5rem 0.4rem", position: "relative" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.8rem", padding: "0 0.5rem 0.4rem", fontSize: "0.7rem", color: BRAND.muted }}>
        {series.map((s) => (
          <span key={s.artist.id} style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: s.color }} />
            {s.artist.name}
          </span>
        ))}
        <span style={{ marginLeft: "auto", color: BRAND.dim }}>┄ target {target}:00</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: "480px", display: "block" }}>
          {Array.from({ length: Math.floor(yMax / yStep) + 1 }, (_, i) => i * yStep).map((m) => (
            <g key={m}>
              <line x1={padL} x2={W - padR} y1={y(m)} y2={y(m)} stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
              <text x={padL - 6} y={y(m) + 3} textAnchor="end" fill="rgba(255,218,173,0.4)" fontSize="9" fontFamily="Fredoka, sans-serif">{m}m</text>
            </g>
          ))}
          <line x1={padL} x2={W - padR} y1={y(target)} y2={y(target)} stroke={BRAND.cream} strokeWidth="1" strokeDasharray="5 4" opacity="0.5" />
          <text x={padL} y={H - 8} fill="rgba(255,218,173,0.4)" fontSize="9" fontFamily="Fredoka, sans-serif">{fmtD(tMin)}</text>
          <text x={W - padR} y={H - 8} textAnchor="end" fill="rgba(255,218,173,0.4)" fontSize="9" fontFamily="Fredoka, sans-serif">{fmtD(tMax)}</text>
          {series.map((s) => {
            const pts = s.pts;
            const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.min).toFixed(1)}`).join(" ");
            const last = pts[pts.length - 1];
            return (
              <g key={s.artist.id}>
                <path d={path} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                {pts.map((p, i) => (
                  <circle
                    key={i} cx={x(p.t)} cy={y(p.min)} r="4.5"
                    fill={s.color} stroke={BRAND.panel} strokeWidth="2"
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setTip({ lx: (x(p.t) / W) * 100, ly: (y(p.min) / H) * 100, text: `${s.artist.name} · ${fmtD(p.t)}${p.event ? ` · ${p.event}` : ""} — avg ${p.min.toFixed(1)}m over ${p.n}` })}
                    onMouseLeave={() => setTip(null)}
                    onClick={() => setTip({ lx: (x(p.t) / W) * 100, ly: (y(p.min) / H) * 100, text: `${s.artist.name} · ${fmtD(p.t)} — avg ${p.min.toFixed(1)}m over ${p.n}` })}
                  />
                ))}
                <circle cx={x(last.t) + 10} cy={y(last.min)} r="4" fill={s.color} />
                <text x={x(last.t) + 17} y={y(last.min) + 3} fill="rgba(255,218,173,0.75)" fontSize="10" fontWeight="700" fontFamily="Fredoka, sans-serif">
                  {s.artist.name}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {tip && (
        <div style={{
          position: "absolute", left: `${tip.lx}%`, top: `${tip.ly}%`,
          transform: "translate(-50%, -130%)", pointerEvents: "none",
          background: "rgba(6,12,32,0.95)", border: `1px solid ${BRAND.border}`,
          color: BRAND.text, borderRadius: "8px", padding: "0.3rem 0.55rem",
          fontSize: "0.68rem", whiteSpace: "nowrap", zIndex: 5,
        }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}

function ModeChip({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      background: active ? BRAND.gold : "transparent",
      color: active ? BRAND.dark : BRAND.muted,
      border: active ? `1px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
      borderRadius: "999px", padding: "0.25rem 0.7rem", fontSize: "0.72rem",
      fontWeight: 800, cursor: "pointer",
    }}>
      {label}
    </button>
  );
}

function Label({ children, style }) {
  return (
    <div style={{
      color: "rgba(255,218,173,0.55)", fontFamily: FONT.display, fontSize: "0.7rem",
      fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", margin: "0 0 0.5rem", ...style,
    }}>
      {children}
    </div>
  );
}
